<template>
  <section
    ref="level"
    class="level"
    :style="{ height: layout.levelHeight + 'px' }"
    aria-label="An introduction to Davie, told as a side-scrolling level"
  >
    <div ref="stage" class="stage" :class="{ 'is-night': isNight }">
      <!-- Sky: gradient, sun and moon, stars and soft color orbs -->
      <div class="sky" aria-hidden="true">
        <div ref="stars" class="stars">
          <i v-for="(star, i) in stars" :key="i" :style="star"></i>
        </div>
        <div ref="sun" class="sun"></div>
        <div ref="moon" class="moon"></div>
        <div class="orb orb-a"></div>
        <div class="orb orb-b"></div>
      </div>

      <!-- Hazy parallax landscape, blurred more the further away it is -->
      <div
        v-for="layer in backLayers"
        :key="layer.key"
        :ref="el => (layerEls[layer.key] = el)"
        class="layer"
        :class="'layer-' + layer.key"
        :style="layer.style"
        aria-hidden="true"
      >
        <svg :viewBox="`0 0 ${layer.width} 100`" preserveAspectRatio="none">
          <path :d="layer.d" />
        </svg>
      </div>
      <div class="fog" aria-hidden="true"></div>

      <!-- The playable world: ground, enemies, flagpole and castle -->
      <div ref="world" class="world" :style="{ width: layout.worldWidth + 'px' }" aria-hidden="true">
        <div class="ground" :style="groundStyle"></div>
        <div
          v-for="(e, i) in layout.enemies"
          :key="i"
          class="enemy-slot"
          :style="{
            left: e.x + 'px',
            top: layout.groundTop - layout.enemyH + 'px',
            width: layout.enemyW + 'px',
            height: layout.enemyH + 'px',
          }"
        >
          <img
            v-if="i < hits"
            class="pixel coin-pop"
            :src="art.coin"
            alt=""
            :style="{ width: layout.s * 10 + 'px', height: layout.s * 13 + 'px' }"
          />
          <span
            :ref="el => (enemyEls[i] = el)"
            class="pixel enemy"
            :style="{
              backgroundImage: `url(${art.enemies[i]})`,
              backgroundSize: `${layout.enemyW * 3}px ${layout.enemyH}px`,
              '--fw': layout.enemyW + 'px',
              animationDelay: -i * 0.23 + 's',
            }"
          ></span>
        </div>
        <img
          ref="burst"
          class="pixel burst"
          :src="art.burst"
          alt=""
          :style="{ width: layout.s * 13 + 'px', height: layout.s * 13 + 'px' }"
        />

        <div class="flagpole" :style="poleStyle">
          <div class="pole-ball"></div>
          <img ref="flag" class="pixel flag" :src="art.flag" alt="" :style="flagStyle" />
        </div>
        <img
          class="pixel castle"
          :src="art.castle"
          alt=""
          :style="{
            left: layout.castleX + 'px',
            top: layout.groundTop - layout.castleH + 'px',
            width: layout.castleW + 'px',
            height: layout.castleH + 'px',
          }"
        />
      </div>

      <button ref="davie" class="davie" type="button" aria-label="Make Davie throw a punch" @click="jab">
        <span ref="sprite" class="pixel davie-sprite" :style="spriteStyle"></span>
      </button>

      <div
        v-for="layer in foreLayers"
        :key="layer.key"
        :ref="el => (layerEls[layer.key] = el)"
        class="layer"
        :class="'layer-' + layer.key"
        :style="layer.style"
        aria-hidden="true"
      >
        <svg :viewBox="`0 0 ${layer.width} 100`" preserveAspectRatio="none">
          <path :d="layer.d" />
        </svg>
      </div>

      <!-- Title screen -->
      <div ref="hero" class="hero">
        <h1 class="display hero-title">
          {{ profile.greeting }}<br />
          <em>{{ profile.subGreeting }}</em>
        </h1>
        <p class="hero-lede">{{ profile.lede }}</p>
      </div>
      <p ref="hint" class="pixel-font scroll-hint">Scroll to press start <span>▼</span></p>

      <!-- Chapter text that appears as each enemy is knocked out -->
      <Transition name="card">
        <article v-if="chapter" :key="chapter.key" class="card" :style="{ '--n': chapter.title.length }">
          <div class="card-text">
            <p class="pixel-font card-meta">
              <img class="pixel card-icon" :src="art.icons[chapter.icon]" alt="" />
              <span>{{ chapter.quest }}</span>
              <span class="card-dot">·</span>
              <span>{{ chapter.label }}</span>
            </p>
            <h2 class="display card-title">
              <template v-for="(word, wi) in chapterWords" :key="wi">
                <span class="word"><span
                  v-for="c in word"
                  :key="c.i"
                  class="ch"
                  :style="{ '--i': c.i }"
                >{{ c.ch }}</span></span>{{ wi < chapterWords.length - 1 ? ' ' : '' }}
              </template>
            </h2>
            <p class="card-body">{{ chapter.body }}</p>
            <a
              v-if="chapter.media.url"
              class="pixel-font card-link"
              :href="chapter.media.url"
              target="_blank"
              rel="noopener"
            >Visit {{ chapter.media.domain }} ↗</a>
          </div>

          <div v-if="chapter.media.type === 'shot'" class="card-media shot-wrap">
            <a
              class="shot"
              :href="chapter.media.url"
              target="_blank"
              rel="noopener"
              :aria-label="'Open ' + chapter.media.domain"
            >
              <span class="shot-bar" aria-hidden="true"><i></i><i></i><i></i><b>{{ chapter.media.domain }}</b></span>
              <img :src="chapter.media.src" :alt="'Screenshot of ' + chapter.media.domain" />
            </a>
            <span v-if="chapter.media.logo" class="shot-logo" :class="{ 'on-dark': chapter.media.logoOnDark }">
              <img :src="chapter.media.logo" :alt="chapter.label + ' logo'" />
            </span>
          </div>
          <figure v-else-if="chapter.media.type === 'photo'" class="card-media photo">
            <img :src="chapter.media.src" :alt="chapter.media.caption" />
            <span class="shot-logo"><img :src="chapter.media.logo" :alt="chapter.label + ' logo'" /></span>
            <figcaption class="pixel-font">{{ chapter.media.caption }}</figcaption>
          </figure>
          <ul v-else class="card-media covers">
            <li v-for="book in chapter.media.items" :key="book.title">
              <img :src="book.src" :alt="'Cover of ' + book.title" />
              <p class="cover-title">{{ book.title }}</p>
              <p class="cover-author">{{ book.author }}</p>
            </li>
          </ul>
        </article>
      </Transition>

      <Transition name="clear">
        <div v-if="cleared" class="clear-banner">
          <p class="pixel-font clear-title">Course clear!</p>
          <p class="display clear-sub"><em>Thanks for playing.</em> There's a little more below.</p>
        </div>
      </Transition>

      <header class="pixel-font hud">
        <div class="hud-cell">
          <span class="hud-label">Davie</span>
          <span>{{ score }}</span>
        </div>
        <div class="hud-cell">
          <span class="hud-label">Coins</span>
          <span class="hud-coins"><img class="pixel" :src="art.coin" alt="" />×{{ String(hits).padStart(2, '0') }}</span>
        </div>
        <div class="hud-cell hud-world">
          <span class="hud-label">World</span>
          <span>1-{{ Math.max(hits, 1) }}</span>
        </div>
        <div class="hud-actions">
          <button type="button" class="hud-btn" :aria-pressed="soundOn" @click="toggleSound">
            ♪ {{ soundOn ? 'On' : 'Off' }}
          </button>
          <a class="hud-btn" :href="profile.linkedin" target="_blank" rel="noopener">Connect ↗</a>
        </div>
      </header>

      <div ref="fade" class="fade-out" aria-hidden="true"></div>
    </div>
  </section>
</template>

<script>
import { profile, chapters } from '../content'
import { DAVIE_FRAMES, FRAME_W, ENEMIES, BURST, COIN, GRASS, DIRT, CASTLE, FLAG, ICONS } from '../game/sprites'
import { spriteUrl, sheetUrl } from '../game/pixels'
import { ridgePath, pinesPath, blobsPath } from '../game/landscape'
import { sound } from '../game/sound'

// Share of the section's scroll spent walking; the rest fades into the next section.
const WALK_END = 0.94

// Sprite sheet columns (see DAVIE_FRAMES)
const FRAME = { idle: 0, blink: 1, walk: 2, jump: 6, punch: 7 }

const clamp = (v, lo = 0, hi = 1) => Math.min(hi, Math.max(lo, v))

function shuffle(list) {
  const out = [...list]
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[out[i], out[j]] = [out[j], out[i]]
  }
  return out
}
const smooth = (a, b, v) => {
  const t = clamp((v - a) / (b - a))
  return t * t * (3 - 2 * t)
}

// Time of day across the level: misty morning → clear day → dusk → night.
const SKY_STOPS = [
  { at: 0, skyTop: '#86aca6', skyBottom: '#ecdcaa', far: '#9ab5a7', mid: '#72968d', near: '#46736a', trees: '#2d5048', fore: '#16302b', fog: '#ecdfb8' },
  { at: 0.4, skyTop: '#6ea2c2', skyBottom: '#dcebe4', far: '#94b6c0', mid: '#6592a0', near: '#437373', trees: '#29504a', fore: '#13302c', fog: '#e3efec' },
  { at: 0.72, skyTop: '#5c5588', skyBottom: '#f2a877', far: '#c28f93', mid: '#8d6c80', near: '#574864', trees: '#2f2940', fore: '#1a1626', fog: '#f2ab84' },
  { at: 1, skyTop: '#0a0f22', skyBottom: '#27315c', far: '#2b3459', mid: '#1f2747', near: '#171d37', trees: '#0e1326', fore: '#070a16', fog: '#3c4678' },
]
const COLOR_KEYS = ['skyTop', 'skyBottom', 'far', 'mid', 'near', 'trees', 'fore', 'fog']

const hexToRgb = (hex) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16))

function skyAt(u) {
  let i = 0
  while (i < SKY_STOPS.length - 2 && u > SKY_STOPS[i + 1].at) i++
  const a = SKY_STOPS[i]
  const b = SKY_STOPS[i + 1]
  const t = clamp((u - a.at) / (b.at - a.at))
  const out = {}
  COLOR_KEYS.forEach((k) => {
    const ca = hexToRgb(a[k])
    const cb = hexToRgb(b[k])
    out[k] = `rgb(${ca.map((v, j) => Math.round(v + (cb[j] - v) * t)).join(',')})`
  })
  return out
}

// Parallax layers: factor is how fast the layer moves relative to the ground.
const LAYERS = [
  { key: 'far', factor: 0.05, height: 0.66, bottom: 0.4, blur: 4, shape: (w) => ridgePath(w, { seed: 11, base: 60, amp: 26, roughness: 1.3, jagged: 0.5 }) },
  { key: 'mid', factor: 0.14, height: 0.5, bottom: 0.5, blur: 2, shape: (w) => ridgePath(w, { seed: 23, base: 48, amp: 24, roughness: 2.2, jagged: 0.7 }) },
  { key: 'near', factor: 0.32, height: 0.34, bottom: 0.6, blur: 0.8, shape: (w) => ridgePath(w, { seed: 37, base: 50, amp: 18, roughness: 3, jagged: 0.3 }) },
  { key: 'trees', factor: 0.58, height: 0.2, bottom: 0.8, blur: 0, shape: (w) => pinesPath(w, { seed: 5, base: 14, minH: 30, maxH: 64 }) },
  { key: 'fore', factor: 1.45, height: 0.22, bottom: -0.3, blur: 12, front: true, shape: (w) => blobsPath(w, { seed: 71, base: 30, minR: 110, maxR: 260, gap: 420, squash: 0.28 }) },
]

export default {
  name: 'LevelStage',
  data() {
    return {
      profile,
      chapters,
      layout: { levelHeight: 4000, worldWidth: 0, enemies: [], enemyW: 80, enemyH: 70, s: 5, groundTop: 0, groundH: 0 },
      backLayers: [],
      foreLayers: [],
      stars: [],
      hits: 0,
      active: -1,
      cleared: false,
      isNight: false,
      soundOn: sound.enabled,
      art: { icons: {}, enemies: [] },
    }
  },
  computed: {
    chapter() {
      return this.active >= 0 ? this.chapters[this.active] : null
    },
    chapterWords() {
      if (!this.chapter) return []
      let i = 0
      return this.chapter.title.split(' ').map((word) => {
        const chars = [...word].map((ch) => ({ ch, i: i++ }))
        i++ // the space
        return chars
      })
    },
    score() {
      return String(this.hits * 1000).padStart(6, '0')
    },
    spriteStyle() {
      const { s } = this.layout
      return {
        width: FRAME_W * s + 'px',
        height: 24 * s + 'px',
        backgroundImage: `url(${this.art.davie})`,
        backgroundSize: `${FRAME_W * s * 8}px ${24 * s}px`,
        // Flip around the body's center, not the wider punch frame's.
        transformOrigin: `${8 * s}px 50%`,
      }
    },
    groundStyle() {
      const { s, groundTop } = this.layout
      return {
        top: groundTop + 'px',
        backgroundImage: `url(${this.art.grass}), url(${this.art.dirt})`,
        backgroundSize: `${16 * s}px ${8 * s}px, ${16 * s}px ${8 * s}px`,
        backgroundPosition: `0 0, 0 ${8 * s}px`,
      }
    },
    poleStyle() {
      const l = this.layout
      return { left: l.flagX + 'px', top: l.groundTop - l.poleH + 'px', height: l.poleH + 'px', '--s': l.s + 'px' }
    },
    flagStyle() {
      const { s } = this.layout
      return { width: 14 * s + 'px', height: 7 * s + 'px' }
    },
  },
  created() {
    // Per-frame state lives outside Vue's reactivity on purpose.
    this.layerEls = {}
    this.enemyEls = []
    this.geo = null
    this.off = 0
    this.face = 1
    this.jabStart = 0
    this.lastU = -1
    this.lastT = 0
    this.airborne = false
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    const f = DAVIE_FRAMES
    this.art = {
      davie: sheetUrl([f.idle, f.blink, f.walk0, f.walk1, f.walk2, f.walk3, f.jump, f.punch]),
      burst: spriteUrl(BURST),
      // A different random line-up of opponents on every visit
      enemies: (() => {
        const pool = shuffle(Object.keys(ENEMIES))
        return chapters.map((_, i) => sheetUrl(ENEMIES[pool[i % pool.length]]))
      })(),
      coin: spriteUrl(COIN),
      grass: spriteUrl(GRASS),
      dirt: spriteUrl(DIRT),
      castle: spriteUrl(CASTLE),
      flag: spriteUrl(FLAG),
      icons: Object.fromEntries(Object.entries(ICONS).map(([k, rows]) => [k, spriteUrl(rows)])),
    }

    this.stars = Array.from({ length: 90 }, () => {
      const size = Math.random() < 0.85 ? 2 : 3
      return {
        left: Math.random() * 100 + '%',
        top: Math.random() * 58 + '%',
        width: size + 'px',
        height: size + 'px',
        opacity: 0.35 + Math.random() * 0.65,
        animationDelay: -Math.random() * 4 + 's',
      }
    })
  },
  mounted() {
    this.measure()
    this.onResize = () => {
      clearTimeout(this.resizeTimer)
      this.resizeTimer = setTimeout(() => {
        const { clientWidth: w, clientHeight: h } = this.$refs.stage
        // Mobile URL bars resize the viewport while scrolling; ignore small height changes.
        if (w !== this.geo.vw || Math.abs(h - this.geo.vh) > 120) this.measure()
      }, 150)
    }
    window.addEventListener('resize', this.onResize)
    this.raf = requestAnimationFrame(this.tick)
  },
  beforeUnmount() {
    cancelAnimationFrame(this.raf)
    window.removeEventListener('resize', this.onResize)
  },
  methods: {
    measure() {
      const stage = this.$refs.stage
      const vw = stage.clientWidth
      const vh = stage.clientHeight
      const mobile = vw < 700
      const s = mobile || vh < 720 ? 4 : 5
      const groundH = Math.round(Math.max(vh * 0.16, 88))
      const groundTop = vh - groundH
      const davieW = 16 * s
      const davieH = 24 * s
      const enemyW = 16 * s
      const enemyH = 14 * s
      const davieX = Math.round(vw * (mobile ? 0.12 : 0.22))
      const spacing = clamp(vw * 0.8, 540, 1100)
      const firstGap = vw * (mobile ? 0.7 : 0.82)
      const H = davieH * (vh < 800 ? 0.6 : 0.75)
      const J = Math.max(davieW * 1.8, spacing * 0.16)
      const start = davieX + davieW / 2

      // `hit` is where Davie's center is when his extended glove reaches the enemy.
      const enemies = chapters.map((_, i) => {
        const cx = start + firstGap + i * spacing
        return { cx, x: cx - enemyW / 2, hit: cx - 18 * s }
      })
      const flagX = enemies[enemies.length - 1].cx + spacing * 0.85
      const poleH = Math.round(davieH * 2.2)
      const cs = s + 2
      const castleX = flagX + Math.max(vw * 0.28, 220)
      const doorX = castleX + 15 * cs
      const T = doorX - start

      this.geo = {
        vw, vh, s, groundTop, davieW, davieH, davieX, spacing, firstGap, H, J, enemies, flagX, T,
        J2: J * 1.4, H2: poleH * 0.55, poleH,
      }
      this.layout = {
        levelHeight: Math.round(vh + (T * 0.95) / WALK_END),
        worldWidth: Math.ceil(castleX + 30 * cs + vw),
        enemies, enemyW, enemyH, s, groundTop, groundH, flagX, poleH,
        castleX, castleW: 30 * cs, castleH: 28 * cs,
      }

      const build = (l) => {
        const width = Math.ceil(vw + T * l.factor + 60)
        return {
          key: l.key,
          factor: l.factor,
          width,
          d: l.shape(width),
          style: {
            width: width + 'px',
            height: Math.round(vh * l.height) + 'px',
            bottom: Math.round(groundH * l.bottom) + 'px',
            filter: l.blur ? `blur(${l.blur}px)` : null,
          },
        }
      }
      this.backLayers = LAYERS.filter((l) => !l.front).map(build)
      this.foreLayers = LAYERS.filter((l) => l.front).map(build)
      this.lastU = -1
    },

    tick(now) {
      this.raf = requestAnimationFrame(this.tick)
      const g = this.geo
      const { level, stage, world, davie, sprite, hero, hint, fade, flag, burst, sun, moon, stars } = this.$refs
      if (!g || !level) return

      const dt = Math.min(0.05, (now - (this.lastT || now)) / 1000)
      this.lastT = now

      const rect = level.getBoundingClientRect()
      const p = clamp(-rect.top / (rect.height - g.vh))
      const target = g.T * clamp(p / WALK_END)
      const prev = this.off
      const k = this.reduced ? 1 : 1 - Math.exp(-dt * 7)
      this.off += (target - this.off) * k
      if (Math.abs(target - this.off) < 0.05) this.off = target
      const off = this.off
      const vel = dt > 0 ? (off - prev) / dt : 0
      const c = off + g.davieX + g.davieW / 2 // Davie's center in world coordinates

      // Fights: Davie jabs as he reaches each enemy, which then flies off spinning.
      let punching = false
      let burstAt = null
      g.enemies.forEach((e, i) => {
        const d = c - e.hit
        if (d > -5 * g.s && d < 6 * g.s) punching = true
        if (d >= 0 && d < 9 * g.s) burstAt = { e, t: d / (9 * g.s) }
        const el = this.enemyEls[i]
        if (!el) return
        const t = clamp(d / (g.spacing * 0.4))
        el.classList.toggle('ko', t > 0)
        el.style.transform = t > 0
          ? `translate3d(${t * g.spacing * 0.5}px, ${-t * g.vh * 0.3}px, 0) rotate(${t * 720}deg) scale(${1 - 0.9 * t})`
          : ''
        // Knocked into the distance: it shrinks and fades before reaching the text.
        el.style.opacity = 1 - smooth(0.45, 0.8, t)
      })
      if (burstAt) {
        const size = 13 * g.s
        const x = burstAt.e.cx - 7 * g.s - size / 2
        const top = g.groundTop - g.davieH + 16 * g.s - size / 2
        burst.style.transform = `translate3d(${x}px, ${top}px, 0) scale(${0.6 + burstAt.t * 0.8})`
        burst.style.opacity = 1 - burstAt.t
      } else {
        burst.style.opacity = 0
      }
      if (this.jabStart) {
        if (now - this.jabStart < 260) punching = true
        else this.jabStart = 0
      }

      // A big jump at the flagpole
      let y = 0
      const fdx = c - g.flagX
      if (Math.abs(fdx) < g.J2) y = Math.max(y, g.H2 * (1 - (fdx / g.J2) ** 2))
      const inAir = y > 1.5
      if (inAir && !this.airborne) sound.jump()
      this.airborne = inAir

      // Discrete game state (reactive, changes rarely)
      const hits = g.enemies.filter((e) => c >= e.hit).length
      if (hits !== this.hits) {
        if (hits > this.hits) sound.punch()
        this.hits = hits
      }
      let active = -1
      g.enemies.forEach((e, i) => {
        if (c >= e.hit - g.spacing * 0.15) active = i
      })
      if (c > g.flagX - g.spacing * 0.3) active = -1
      if (active !== this.active) this.active = active
      const cleared = c > g.flagX + 4
      if (cleared && !this.cleared) sound.clear()
      if (cleared !== this.cleared) this.cleared = cleared

      if (active >= 0) {
        const e = g.enemies[active]
        const r = (c - (e.hit - g.spacing * 0.15)) / (g.spacing * 0.45)
        stage.style.setProperty('--reveal', (r * (this.chapters[active].title.length + 18)).toFixed(2))
      }

      if (vel < -20) this.face = -1
      else if (vel > 20) this.face = 1
      let frame
      if (inAir) frame = FRAME.jump
      else if (punching) frame = FRAME.punch
      else if (Math.abs(vel) > 20) frame = FRAME.walk + (Math.floor(Math.abs(off) / (g.s * 7)) % 4)
      else frame = now % 4200 < 140 ? FRAME.blink : FRAME.idle
      sprite.style.backgroundPosition = `${-frame * FRAME_W * g.s}px 0`
      sprite.style.transform = `scaleX(${this.face})`
      davie.style.transform = `translate3d(${g.davieX}px, ${g.groundTop - g.davieH - y}px, 0)`
      davie.style.opacity = 1 - smooth(WALK_END - 0.004, WALK_END + 0.02, p)

      // Parallax
      world.style.transform = `translate3d(${-off}px, 0, 0)`
      ;[...this.backLayers, ...this.foreLayers].forEach((l) => {
        const el = this.layerEls[l.key]
        if (el) el.style.transform = `translate3d(${-off * l.factor}px, 0, 0)`
      })
      const flagDrop = clamp((fdx + g.J2 * 0.3) / (g.J2 * 1.3))
      flag.style.transform = `translateY(${flagDrop * (g.poleH - 7 * g.s - g.s * 6)}px)`

      // Title screen blurs away as Davie starts walking
      const h = clamp(off / (g.firstGap * 0.55))
      hero.style.opacity = 1 - h
      hero.style.transform = `translate3d(0, ${-h * 40}px, 0)`
      hero.style.filter = h > 0.01 ? `blur(${h * 10}px)` : ''
      hero.style.visibility = h >= 1 ? 'hidden' : ''
      hint.style.opacity = 1 - clamp(off / 80)
      fade.style.opacity = smooth(WALK_END + 0.01, 1, p)

      // Time of day
      const u = off / g.T
      if (Math.abs(u - this.lastU) > 0.0005) {
        this.lastU = u
        const sky = skyAt(u)
        COLOR_KEYS.forEach((key) => stage.style.setProperty('--' + key, sky[key]))
        stage.style.setProperty('--dim', (1 - 0.42 * smooth(0.6, 1, u)).toFixed(3))
        // Keep the sun right of the chapter text so it never sits behind words.
        sun.style.transform = `translate3d(${g.vw * (0.84 + 0.06 * u)}px, ${g.vh * (0.12 + 0.44 * smooth(0.3, 0.9, u))}px, 0)`
        sun.style.opacity = 1 - smooth(0.78, 0.92, u)
        moon.style.opacity = smooth(0.86, 1, u)
        stars.style.opacity = smooth(0.74, 1, u)
        const night = u > 0.6
        if (night !== this.isNight) this.isNight = night
      }
    },

    jab() {
      if (this.jabStart) return
      this.jabStart = performance.now()
      sound.punch()
    },

    toggleSound() {
      this.soundOn = !this.soundOn
      if (this.soundOn) sound.enable()
      else sound.disable()
    },
  },
}
</script>

<style scoped>
.level {
  position: relative;
}

.stage {
  --ink: #0f1f1c;
  --ink-soft: rgba(15, 31, 28, 0.72);
  --reveal: 0;
  --dim: 1;
  position: sticky;
  top: 0;
  height: 100vh;
  height: 100svh;
  overflow: hidden;
  background: var(--skyBottom, #ecdcaa);
  color: var(--ink);
  transition: color 0.6s ease;
}
.stage.is-night {
  --ink: #f6f1e7;
  --ink-soft: rgba(246, 241, 231, 0.78);
}
.stage.is-night .card,
.stage.is-night .clear-sub {
  text-shadow: 0 1px 18px rgba(10, 10, 30, 0.35);
}

/* Sky */
.sky {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, var(--skyTop) 0%, var(--skyBottom) 78%);
}
.stars {
  position: absolute;
  inset: 0;
  opacity: 0;
}
.stars i {
  position: absolute;
  background: #fff;
  animation: twinkle 4s ease-in-out infinite;
}
@keyframes twinkle {
  50% { opacity: 0.2; }
}
.sun,
.moon {
  position: absolute;
  top: 0;
  left: 0;
  width: 150px;
  height: 150px;
  margin: -75px 0 0 -75px;
  border-radius: 50%;
}
.sun {
  background: radial-gradient(circle, #fff6d8 0 32%, rgba(255, 236, 190, 0.55) 45%, rgba(255, 230, 180, 0) 70%);
  filter: blur(6px);
}
.moon {
  left: 78%;
  top: 16%;
  width: 70px;
  height: 70px;
  margin: 0;
  opacity: 0;
  background: radial-gradient(circle at 40% 40%, #fbfbff 0 55%, rgba(220, 228, 255, 0) 72%);
  box-shadow: 0 0 80px 20px rgba(170, 190, 255, 0.25);
}
.orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(70px);
  opacity: 0.55;
  mix-blend-mode: soft-light;
}
.orb-a {
  width: 60vw;
  height: 40vh;
  left: -10vw;
  top: 8vh;
  background: var(--fog);
}
.orb-b {
  width: 50vw;
  height: 36vh;
  right: -12vw;
  top: 26vh;
  background: var(--far);
}

/* Landscape */
.layer {
  position: absolute;
  left: 0;
  will-change: transform;
  pointer-events: none;
}
.layer svg {
  display: block;
  width: 100%;
  height: 100%;
}
.layer-far path { fill: var(--far); }
.layer-mid path { fill: var(--mid); }
.layer-near path { fill: var(--near); }
.layer-trees path { fill: var(--trees); }
.layer-fore { z-index: 4; opacity: 0.92; }
.layer-fore path { fill: var(--fore); }

.fog {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 12%;
  height: 34%;
  background: linear-gradient(180deg, transparent, var(--fog) 55%, transparent);
  opacity: 0.32;
  pointer-events: none;
}

/* World */
.world {
  position: absolute;
  inset: 0 auto 0 0;
  will-change: transform;
  filter: brightness(var(--dim));
}
.pixel {
  image-rendering: pixelated;
  image-rendering: crisp-edges;
}
.ground {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  background-repeat: repeat-x, repeat;
  image-rendering: pixelated;
  box-shadow: inset 0 -40px 60px -20px rgba(0, 0, 0, 0.35);
}
.enemy-slot {
  position: absolute;
}
.enemy {
  display: block;
  width: 100%;
  height: 100%;
  background-repeat: no-repeat;
  animation: enemy-idle 0.7s steps(1) infinite;
  will-change: transform;
}
@keyframes enemy-idle {
  0% { background-position: 0 0; }
  50% { background-position: calc(var(--fw) * -1) 0; }
}
.enemy.ko {
  animation: none;
  background-position: calc(var(--fw) * -2) 0;
}
.burst {
  position: absolute;
  left: 0;
  top: 0;
  opacity: 0;
  pointer-events: none;
}
.coin-pop {
  position: absolute;
  left: 50%;
  bottom: 100%;
  transform: translateX(-50%);
  animation: coin-pop 0.7s cubic-bezier(0.2, 0.7, 0.3, 1) forwards;
}
@keyframes coin-pop {
  0% { transform: translate(-50%, 60%) scaleX(1); opacity: 1; }
  25% { transform: translate(-50%, -110%) scaleX(0.2); }
  50% { transform: translate(-50%, -160%) scaleX(1); opacity: 1; }
  75% { transform: translate(-50%, -140%) scaleX(0.2); opacity: 1; }
  100% { transform: translate(-50%, -80%) scaleX(1); opacity: 0; }
}
.flagpole {
  position: absolute;
  width: calc(var(--s) * 1.5);
  background: linear-gradient(90deg, #d8dfd2, #8d998b);
}
.pole-ball {
  position: absolute;
  left: 50%;
  top: calc(var(--s) * -4);
  width: calc(var(--s) * 4);
  height: calc(var(--s) * 4);
  transform: translateX(-50%);
  border-radius: 50%;
  background: #6fbf73;
  box-shadow: inset -3px -3px 0 rgba(0, 0, 0, 0.25);
}
.flag {
  position: absolute;
  right: 100%;
  top: calc(var(--s) * 2);
}
.castle {
  position: absolute;
}

/* Davie */
.davie {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 3;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  will-change: transform;
  filter: brightness(max(var(--dim), 0.78));
}
.davie-sprite {
  display: block;
  background-repeat: no-repeat;
}
.davie::after {
  content: '';
  position: absolute;
  left: 12%;
  right: 32%;
  bottom: -6px;
  height: 8px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.18);
  filter: blur(2px);
}
.davie:focus-visible {
  outline: 2px dashed var(--ink);
  outline-offset: 6px;
}

/* Title screen */
.hero {
  position: absolute;
  z-index: 5;
  left: clamp(16px, 6vw, 96px);
  right: clamp(16px, 6vw, 96px);
  top: clamp(88px, 17vh, 180px);
  max-width: 900px;
  will-change: transform, opacity, filter;
}
.hero-title {
  font-size: clamp(52px, 9vw, 132px);
  line-height: 0.95;
  letter-spacing: -0.02em;
}
.hero-title em {
  font-style: italic;
  opacity: 0.78;
}
.hero-lede {
  margin-top: 28px;
  max-width: 34ch;
  font-size: clamp(16px, 1.4vw, 19px);
  line-height: 1.55;
  color: var(--ink-soft);
}
.scroll-hint {
  position: absolute;
  z-index: 5;
  left: 50%;
  bottom: 22px;
  transform: translateX(-50%);
  font-size: 12px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #f6f1e7;
  white-space: nowrap;
  animation: blink 1.2s steps(2, jump-none) infinite;
}
@keyframes blink {
  50% { color: rgba(246, 241, 231, 0.35); }
}

/* Chapter cards */
.card {
  --shown: clamp(0, calc((var(--reveal) - var(--n)) / 14), 1);
  position: absolute;
  z-index: 5;
  top: clamp(76px, 12vh, 150px);
  left: clamp(16px, 6vw, 96px);
  right: clamp(16px, 6vw, 96px);
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.05fr);
  gap: clamp(24px, 5vw, 80px);
  align-items: start;
}
.card-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--ink-soft);
  margin-bottom: 18px;
}
.card-icon {
  width: 36px;
  height: auto;
}
.card-dot {
  opacity: 0.5;
}
.card-title {
  font-size: clamp(30px, min(3.8vw, 6vh), 60px);
  line-height: 1.02;
  letter-spacing: -0.015em;
}
.word {
  white-space: nowrap;
}
.ch {
  opacity: clamp(0.14, calc(var(--reveal) - var(--i)), 1);
}
.card-body,
.card-link {
  opacity: var(--shown);
  transform: translateY(calc(12px - var(--shown) * 12px));
}
.card-body {
  margin-top: 18px;
  max-width: 46ch;
  font-size: clamp(15px, min(1.2vw, 2vh), 18px);
  line-height: 1.6;
  color: var(--ink-soft);
}
.card-link {
  display: inline-block;
  margin-top: 18px;
  padding: 10px 12px;
  font-size: 12px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink);
  text-decoration: none;
  border: 1px solid currentColor;
  transition: background 0.2s ease, color 0.2s ease;
}
.card-link:hover {
  background: var(--ink);
  color: var(--skyBottom);
}

/* Media sharpens out of a blur as the chapter reveals */
.card-media {
  opacity: calc(0.2 + var(--shown) * 0.8);
  filter: blur(calc(14px - var(--shown) * 14px));
  transform: translateY(calc(18px - var(--shown) * 18px));
}
.shot-wrap {
  position: relative;
  /* Fixed size so the frame stays near-square however wide the window is */
  width: min(100%, 440px, 52vh);
}
.shot-logo {
  position: absolute;
  left: 20px;
  bottom: -18px;
  display: flex;
  align-items: center;
  height: 44px;
  padding: 0 14px;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 12px 28px -10px rgba(10, 20, 30, 0.45), 0 0 0 1px rgba(0, 0, 0, 0.06);
  max-width: calc(100% - 40px);
  pointer-events: none;
}
.shot-logo.on-dark {
  background: #16181a;
}
.shot-logo img {
  display: block;
  height: 22px;
  width: auto;
  max-width: 100%;
  object-fit: contain;
}
.shot {
  display: block;
  overflow: hidden;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 30px 60px -20px rgba(10, 20, 30, 0.45), 0 0 0 1px rgba(0, 0, 0, 0.06);
  transition: box-shadow 0.3s ease;
}
.shot:hover {
  box-shadow: 0 36px 70px -20px rgba(10, 20, 30, 0.55), 0 0 0 1px rgba(0, 0, 0, 0.1);
}
.shot-bar {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 12px;
  background: #eeece6;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.shot-bar i {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #cfcbc2;
}
.shot-bar b {
  margin: 0 auto;
  font-family: var(--font-sans);
  font-size: 11px;
  font-weight: 500;
  color: #6b675f;
}
.shot img {
  display: block;
  width: 100%;
  aspect-ratio: 5 / 4;
  object-fit: cover;
  object-position: top;
}
.photo {
  position: relative;
  width: fit-content;
  margin: 0;
}
.photo > img {
  display: block;
  height: min(40vh, 400px);
  width: auto;
  aspect-ratio: 808 / 798;
  border-radius: 10px;
  box-shadow: 0 30px 60px -20px rgba(10, 20, 30, 0.45), 0 0 0 1px rgba(0, 0, 0, 0.06);
  transform: rotate(1.5deg);
}
.photo .shot-logo {
  height: 60px;
  padding: 0 8px;
}
.photo .shot-logo img {
  height: 48px;
}
.photo figcaption {
  position: absolute;
  right: 12px;
  bottom: -30px;
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--ink-soft);
}
.covers {
  display: flex;
  gap: clamp(16px, 2.4vw, 32px);
  list-style: none;
}
/* Covers share the column width, capped so they never grow too tall */
.covers li {
  flex: 1 1 0;
  min-width: 0;
  max-width: min(200px, 17.3vh);
}
.covers img {
  display: block;
  width: 100%;
  height: auto;
  aspect-ratio: 2 / 3;
  object-fit: cover;
  border-radius: 3px;
  box-shadow: 0 24px 40px -16px rgba(10, 10, 30, 0.55), 0 0 0 1px rgba(0, 0, 0, 0.08);
  transform: rotate(-2deg);
}
.covers li:nth-child(even) img {
  transform: rotate(2deg);
}
.cover-title {
  margin-top: 14px;
  font-family: var(--font-display);
  font-size: clamp(18px, 1.5vw, 22px);
  line-height: 1.1;
}
.cover-author {
  margin-top: 4px;
  font-size: 13px;
  color: var(--ink-soft);
}
.card-enter-active,
.card-leave-active {
  transition: opacity 0.5s ease, filter 0.5s ease, transform 0.5s ease;
}
.card-enter-from,
.card-leave-to {
  opacity: 0;
  filter: blur(12px);
}
.card-enter-from { transform: translateY(16px); }
.card-leave-to { transform: translateY(-16px); }

/* Course clear */
.clear-banner {
  position: absolute;
  z-index: 5;
  left: 0;
  right: 0;
  top: clamp(100px, 20vh, 220px);
  padding: 0 16px;
  text-align: center;
}
.clear-title {
  font-size: clamp(26px, 4.4vw, 56px);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #ffe9a8;
  text-shadow: 4px 4px 0 #6b3d14;
}
.clear-sub {
  margin-top: 16px;
  font-size: clamp(20px, 2.4vw, 30px);
  color: var(--ink);
}
.clear-enter-active {
  transition: opacity 0.4s ease, transform 0.5s cubic-bezier(0.2, 1.4, 0.4, 1);
}
.clear-leave-active {
  transition: opacity 0.3s ease;
}
.clear-enter-from {
  opacity: 0;
  transform: scale(0.8);
}
.clear-leave-to {
  opacity: 0;
}

/* HUD */
.hud {
  position: absolute;
  z-index: 6;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: flex-start;
  gap: clamp(16px, 4vw, 56px);
  padding: 18px clamp(16px, 4vw, 48px);
  font-size: 12px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink);
}
.hud-cell {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.hud-label {
  color: var(--ink-soft);
}
.hud-coins {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.hud-coins img {
  width: 10px;
  height: 13px;
}
.hud-actions {
  margin-left: auto;
  display: flex;
  gap: 8px;
}
.hud-btn {
  font: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  color: inherit;
  text-decoration: none;
  background: transparent;
  border: 1px solid currentColor;
  padding: 8px 10px;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
}
.hud-btn:hover {
  background: var(--ink);
  color: var(--skyBottom);
}

.fade-out {
  position: absolute;
  inset: 0;
  z-index: 7;
  background: var(--bg-deep);
  opacity: 0;
  pointer-events: none;
}

@media (max-width: 699px) {
  .hud-world {
    display: none;
  }
  .hud {
    gap: 16px;
    font-size: 10px;
  }
  .hud-btn {
    padding: 7px 8px;
  }
  .card {
    top: 76px;
    left: 16px;
    right: 16px;
    grid-template-columns: 1fr;
    gap: 14px;
  }
  .card-meta {
    margin-bottom: 10px;
    font-size: 10px;
  }
  .card-title {
    font-size: 30px;
  }
  .card-body {
    margin-top: 10px;
    font-size: 14px;
    line-height: 1.5;
  }
  .card-link {
    display: none;
  }
  .shot-wrap {
    width: min(100%, 240px, 28vh);
  }
  .shot-logo {
    left: 12px;
    bottom: -12px;
    height: 30px;
    padding: 0 10px;
  }
  .shot-logo img {
    height: 15px;
  }
  .shot-bar {
    height: 20px;
  }
  .covers {
    gap: 12px;
  }
  .covers li {
    max-width: 76px;
  }
  .photo > img {
    height: 200px;
  }
  .photo .shot-logo {
    height: 44px;
    padding: 0 6px;
  }
  .photo .shot-logo img {
    height: 34px;
  }
  .photo figcaption {
    bottom: -24px;
    font-size: 9px;
  }
  .cover-title {
    margin-top: 8px;
    font-size: 14px;
  }
  .cover-author {
    font-size: 11px;
  }
  .hero-title {
    font-size: clamp(40px, 12vw, 56px);
  }
}

/* Short phones: tighten the card so media clears Davie's head */
@media (max-width: 699px) and (max-height: 720px) {
  .card-title {
    font-size: 26px;
  }
  .card-body {
    font-size: 13px;
  }
  .shot-wrap {
    width: min(100%, 22vh);
  }
  .shot-bar {
    height: 16px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .scroll-hint,
  .stars i {
    animation: none;
  }
}
</style>
